import React, { useEffect, useState } from 'react';
import { db } from '../firebase/config';
import { collection, setDoc, doc, addDoc, getDoc } from 'firebase/firestore';

interface Props {
  payments: any[];
  users: any[];
}

interface Config {
  revenueDropThreshold: number; // percent
  churnThreshold: number; // percent
  webhookUrl?: string;
  alertEmail?: string;
}

const DEFAULT_CONFIG: Config = { revenueDropThreshold: 20, churnThreshold: 5, webhookUrl: '', alertEmail: '' };

const AdminInsights: React.FC<Props> = ({ payments, users }) => {
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const cfgRef = doc(db, 'admin', 'insights_config');
        const cfgSnap = await getDoc(cfgRef);
        if (cfgSnap.exists()) {
          const data = cfgSnap.data() as any;
          setConfig(c => ({ ...c, ...(data || {}) }));
        }
      } catch (err) {
        console.error('Erro ao carregar config de insights', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  // compute metrics similarly to previous

  const now = new Date();
  const buckets = [0, 0]; // [prevMonth, lastMonth]
  payments.forEach(p => {
    const created = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
    if (!created) return;
    const diffMonths = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
    if (diffMonths === 0) buckets[1] += Number(p.amount || 0);
    if (diffMonths === 1) buckets[0] += Number(p.amount || 0);
  });
  const revenueChange = buckets[0] === 0 ? (buckets[1] > 0 ? 100 : 0) : ((buckets[1] - buckets[0]) / buckets[0]) * 100;

  const thirtyDays = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
  const churned = users.filter(u => u.premiumActivatedAt && u.premiumExpiresAt && new Date(u.premiumExpiresAt) > thirtyDays && !u.isPremium).length;
  const churnRate = users.length ? (churned / users.length) * 100 : 0;

  const insights = [] as { type: 'info'|'warning'|'critical', text: string, key?: string }[];
  if (revenueChange < -config.revenueDropThreshold) insights.push({ type: 'critical', text: `Receita caiu ${Math.round(-revenueChange)}% em relação ao mês anterior.`, key: 'revenue_drop' });
  else if (revenueChange > config.revenueDropThreshold) insights.push({ type: 'info', text: `Boa! Receita aumentou ${Math.round(revenueChange)}% em relação ao mês anterior.`, key: 'revenue_up' });
  else insights.push({ type: 'info', text: `Variação da receita: ${Math.round(revenueChange)}%`, key: 'revenue_same' });

  if (churnRate > config.churnThreshold) insights.push({ type: 'warning', text: `Taxa de churn estimada em ${churnRate.toFixed(1)}% — verifique cancelamentos.`, key: 'churn_high' });

  const saveConfig = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'admin', 'insights_config'), { ...config, updatedAt: new Date().toISOString() }, { merge: true });
      // write log
      await addDoc(collection(db, 'admin_logs'), { level: 'info', message: 'Config de insights atualizada', createdAt: new Date().toISOString() });
      alert('Configurações salvas.');
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const checkAndNotify = async () => {
    // for each insight that is critical or warning, send a webhook/email if configured
    for (const ins of insights) {
      if (ins.type === 'critical' || ins.type === 'warning') {
        const payload = { type: ins.type, text: ins.text, timestamp: new Date().toISOString() };
        if (config.webhookUrl) {
          try {
            await fetch(config.webhookUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            await addDoc(collection(db, 'admin_logs'), { level: 'info', message: `Webhook enviado: ${ins.text}`, createdAt: new Date().toISOString() });
          } catch (err) {
            console.error('Erro enviando webhook', err);
            await addDoc(collection(db, 'admin_logs'), { level: 'error', message: `Erro enviando webhook: ${String((err as any)?.message || err)}`, createdAt: new Date().toISOString() });
          }
        }
        if (config.alertEmail) {
          // enqueue a notification doc for backend/email worker to process
          try {
            await addDoc(collection(db, 'notifications'), { to: config.alertEmail, subject: `Alerta: ${ins.type}`, body: ins.text, createdAt: new Date().toISOString(), status: 'pending' });
            await addDoc(collection(db, 'admin_logs'), { level: 'info', message: `Notificação por e-mail agendada para ${config.alertEmail}`, createdAt: new Date().toISOString() });
          } catch (err) {
            console.error('Erro agendando e-mail', err);
            await addDoc(collection(db, 'admin_logs'), { level: 'error', message: `Erro agendando e-mail: ${String((err as any)?.message || err)}`, createdAt: new Date().toISOString() });
          }
        }
      }
    }
    alert('Verificação concluída — notificações enviadas se houverem alertas.');
  };

  return (
    <div className="section-block">
      <h3>Insights</h3>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <label>
          <div className="muted">Queda de receita (%)</div>
          <input type="number" value={config.revenueDropThreshold} onChange={e => setConfig(c => ({ ...c, revenueDropThreshold: Number(e.target.value) }))} />
        </label>
        <label>
          <div className="muted">Churn (%)</div>
          <input type="number" value={config.churnThreshold} onChange={e => setConfig(c => ({ ...c, churnThreshold: Number(e.target.value) }))} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <label style={{ flex: 1 }}>
          <div className="muted">Webhook URL</div>
          <input placeholder="https://hooks.exemplo.com/notify" value={config.webhookUrl} onChange={e => setConfig(c => ({ ...c, webhookUrl: e.target.value }))} />
        </label>
        <label>
          <div className="muted">Alert e-mail</div>
          <input placeholder="alerts@empresa.com" value={config.alertEmail} onChange={e => setConfig(c => ({ ...c, alertEmail: e.target.value }))} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={saveConfig} className="primary" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Config'}</button>
        <button onClick={checkAndNotify} disabled={loading}>Verificar e Notificar</button>
      </div>

      {insights.length === 0 ? <p className="muted">Nenhum insight crítico no momento.</p> : (
        <ul>
          {insights.map((i, idx) => (
            <li key={idx} style={{ marginBottom: 8 }}>
              <strong style={{ color: i.type === 'critical' ? '#dc2626' : i.type === 'warning' ? '#f59e0b' : '#0f172a' }}>{i.text}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminInsights;